/**
 * Student Information app — canonical field catalog.
 *
 * - Each `key` must match a column on `student_info` (except keys are the API/JSON field names = column names here).
 * - `enabled_attributes` on the app_assignment relationship is an ordered string[] of these keys;
 *   User Profile renders fields in that array order (see UserProfileView).
 * - Adding a new attribute: add a migration altering `student_info`, extend this array + UserProfileView render branch,
 *   and `api/student-info.js` PUT handler.
 *
 * DB schema source: migrations/create_student_info.sql
 */

export const STUDENT_INFORMATION_TABLE = 'student_info';

/**
 * Ordered catalog: default profile order when assignment metadata omits enabled_attributes.
 * @type {Array<{
 *   key: string,
 *   label: string,
 *   dbColumn: string,
 *   dbType: string,
 *   uiInputType: string,
 *   uiDetail?: string,
 *   maskable?: boolean
 * }>}
 */
export const STUDENT_INFORMATION_FIELD_DEFS = [
  {
    key: 'status',
    label: 'Enrollment status',
    dbColumn: 'status',
    dbType: 'varchar(64)',
    uiInputType: 'select',
    uiDetail: 'Badge picker on profile. Options from assignment metadata status_options (Apps Manager); default Active, Withdrawn, Exited, Completed. Ended statuses may prompt for exit_date.'
  },
  {
    key: 'enrollment_date',
    label: 'Enrollment date',
    dbColumn: 'enrollment_date',
    dbType: 'date',
    uiInputType: 'date',
    uiDetail: 'Point-in-time enrolled; distinct from ugp / period window (see appSlugs docs).'
  },
  {
    key: 'exit_date',
    label: 'Exit date',
    dbColumn: 'exit_date',
    dbType: 'date',
    uiInputType: 'date',
    uiDetail: 'Actual leave date when status is ended; not inferred from ugp.end_date.'
  },
  {
    key: 'grade_level',
    label: 'Grade level',
    dbColumn: 'grade_level',
    dbType: 'varchar(128)',
    uiInputType: 'select | text',
    uiDetail: 'Badge picker on profile. Options from assignment metadata grade_level_options; empty = free text in picker.'
  },
  {
    key: 'previously_enrolled',
    label: 'Previously Enrolled?',
    /** Shown on profile badge when toggled on (no question mark). */
    onLabel: 'Previously Enrolled',
    dbColumn: 'previously_enrolled',
    dbType: 'boolean',
    uiInputType: 'checkbox'
  },
  {
    key: 'transfer_credits',
    label: 'Transfer Credits',
    dbColumn: 'transfer_credits',
    dbType: 'numeric(10,2)',
    uiInputType: 'number',
    uiDetail: 'Badge on profile: "Transfer Credits?" when empty; "{n} Transfer Credits" when set (click to edit).'
  },
  {
    key: 'vendor_groups_periods_id',
    label: 'Vendor partner',
    dbColumn: 'vendor_groups_periods_id',
    dbType: 'uuid → groups_periods',
    uiInputType: 'select',
    uiDetail:
      'Team group-period (e.g. FA01) for vendor / work-sample routing. Not membership. Changes append to update_history.'
  },
  {
    key: 'track_period_id',
    label: 'Schedule track',
    dbColumn: 'track_period_id',
    dbType: 'uuid → periods (scheduler, type=track)',
    uiInputType: 'select',
    uiDetail:
      'Scheduler track period for LP calendar and schedule resolution. Changes append to update_history.'
  },
  {
    key: 'primary_pathway',
    label: 'Primary pathway',
    dbColumn: 'primary_pathway',
    dbType: 'uuid → taxonomy (type=concentration)',
    uiInputType: 'select',
    uiDetail:
      'Primary CTE pathway / concentration from the shared taxonomy catalog. Changes append to update_history.'
  }
];

export const TRANSFER_CREDITS_EMPTY_LABEL = 'Transfer Credits?';

export function hasTransferCreditsValue(value) {
  if (value == null || String(value).trim() === '') return false;
  const n = parseFloat(String(value).trim());
  return !Number.isNaN(n);
}

/** e.g. "12 Transfer Credits" */
export function formatTransferCreditsBadgeValue(value) {
  if (!hasTransferCreditsValue(value)) return null;
  const n = parseFloat(String(value).trim());
  const formatted = Number.isInteger(n) ? String(n) : String(n);
  const noun = n === 1 ? 'Transfer Credit' : 'Transfer Credits';
  return `${formatted} ${noun}`;
}

const KEY_ORDER = STUDENT_INFORMATION_FIELD_DEFS.map(d => d.key);
const KEY_SET = new Set(KEY_ORDER);

export const STUDENT_INFORMATION_FIELD_KEYS = [...KEY_ORDER];

/** When assignment metadata has no enabled_attributes array, User Profile uses this ordered list. */
export const DEFAULT_STUDENT_INFO_ENABLED_ATTRIBUTES = [...KEY_ORDER];

/** Same as FIELD_KEYS; kept for imports that expect ATTRIBUTE_KEYS. */
export const STUDENT_INFORMATION_ATTRIBUTE_KEYS = [...KEY_ORDER];

export const STUDENT_INFO_MASKABLE_ATTRIBUTE_KEYS = STUDENT_INFORMATION_FIELD_DEFS.filter(d => d.maskable).map(d => d.key);

export function getStudentInformationFieldDef(key) {
  return STUDENT_INFORMATION_FIELD_DEFS.find(d => d.key === key) || null;
}

/** Normalize saved enabled_attributes: drop unknown keys, preserve order. */
export function normalizeStudentEnabledAttributes(saved) {
  if (!Array.isArray(saved)) return [...DEFAULT_STUDENT_INFO_ENABLED_ATTRIBUTES];
  return saved.filter(k => KEY_SET.has(k));
}

/**
 * Assignment metadata (relationships.metadata) keys used with student_information — beyond enabled_attributes.
 * - exitdate_sync_popup: default true; profile may prompt on ended status (UserProfileView).
 * - withdrawal_sync_ugp_end_date: legacy; passed to PUT /api/student-info as withdrawal_sync_ugp_end_date for ugp end_date sync.
 */
export const STUDENT_INFO_ASSIGNMENT_META = {
  EXITDATE_SYNC_POPUP: 'exitdate_sync_popup',
  WITHDRAWAL_SYNC_UGP: 'withdrawal_sync_ugp_end_date',
  VENDOR_ALLOWED_GP_ROOTS: 'vendor_allowed_gp_roots'
};
